/**
 * 班级报告 Playwright 印刷模式页面（spec §4.3）。
 *
 * 路由：/print/class/:id?token=<hmac>（不嵌 PrivateRoute，HMAC class token 鉴权）。
 *  - id='preview' / 'demo' → 本地 mock，无需 token（本地调样式）。
 *  - 真 id → GET /api/dashboard/classes/<id>/print-data/?token=xxx（@action，无 /report/ 段）。
 *  - 渲染完后双 rAF + 300ms 置 data-report-ready=1，Playwright 同步。
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ClassReport from '../components/report_class/ClassReport';
import { mockClassReportPayload } from '../mocks/classReportMock';

// 原生 fetch（绕 api/client.js 拦截器）：印刷模式只认 HMAC token。
async function fetchPrintData(classId, token) {
  const url = `/api/dashboard/classes/${encodeURIComponent(classId)}/print-data/`
    + `?token=${encodeURIComponent(token)}`;
  const resp = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });
  let body = null;
  try { body = await resp.json(); } catch (_) { body = null; }
  return { ok: resp.ok, status: resp.status, body };
}

export default function PrintClassReportPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id || 'preview';
  const token = searchParams.get('token') || '';

  const [payload, setPayload] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (id === 'preview' || id === 'demo') {
        await new Promise((r) => setTimeout(r, 50));
        if (!cancelled) setPayload(mockClassReportPayload);
        return;
      }
      if (!token) {
        setErrorMsg('缺少 token 参数。印刷模式 URL 形如 /print/class/<id>?token=<hmac>。');
        return;
      }
      const { ok, status, body } = await fetchPrintData(id, token);
      if (cancelled) return;
      if (!ok) {
        setErrorMsg(`拉 print-data 失败 (${status}): ${body?.detail || `HTTP ${status}`}`);
        return;
      }
      setPayload(body);
    }
    load();
    return () => { cancelled = true; };
  }, [id, token]);

  useEffect(() => {
    if (!payload) return undefined;
    let raf1 = 0;
    let raf2 = 0;
    let timer = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        timer = window.setTimeout(() => {
          document.documentElement.dataset.reportReady = '1';
          window.__reportReady = true;
        }, 300);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (timer) window.clearTimeout(timer);
      delete document.documentElement.dataset.reportReady;
      window.__reportReady = false;
    };
  }, [payload]);

  if (errorMsg) {
    return (
      <div style={{ padding: 40, fontFamily: 'sans-serif' }}>
        <h2>印刷模式暂未就绪</h2>
        <p>{errorMsg}</p>
      </div>
    );
  }
  if (!payload) {
    return <div style={{ padding: 40, fontFamily: 'sans-serif' }}>正在加载班级报告…</div>;
  }
  return <ClassReport payload={payload} printMode />;
}
